import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';

import type {
  CalibrationMatrixConsultant,
  CalibrationMatrixGradeConfig,
} from '@/api/types/calibrationMatrix.types';
import { EmptyStateMessage } from '@/components/common/EmptyStateMessage';
import { CalibrationMatrixCell, type CalibrationMatrixCellConsultant } from './CalibrationMatrixCell';
import { deriveBillingGradeGroups, getCellWindowTier } from './helpers';

interface CalibrationMatrixGridProps {
  gradeConfig: CalibrationMatrixGradeConfig;
  consultants: CalibrationMatrixConsultant[];
}

function consultantToCell(
  c: CalibrationMatrixConsultant
): CalibrationMatrixCellConsultant {
  return {
    appraisalId: c.appraisalId,
    firstName: c.firstName,
    lastName: c.lastName,
    geo: c.geo,
  };
}

export function CalibrationMatrixGrid({ gradeConfig, consultants }: CalibrationMatrixGridProps) {
  const groups = useMemo(() => deriveBillingGradeGroups(gradeConfig), [gradeConfig]);
  const columns = gradeConfig.columns;

  const consultantsByCell = useMemo(() => {
    const map = new Map<string, CalibrationMatrixCellConsultant[]>();
    for (const c of consultants) {
      const key = `${c.tenureColumn}|${c.billingGradeRow}|${c.finalRating}`;
      const list = map.get(key) ?? [];
      list.push(consultantToCell(c));
      map.set(key, list);
    }
    return map;
  }, [consultants]);

  const isEmpty = consultants.length === 0;

  const totalSubRows = groups.reduce((sum, g) => sum + g.rowCount, 0);

  return (
    <Box data-testid="calibration-matrix-grid">
      <Box
        role="grid"
        aria-label="Calibration matrix"
        sx={{
          display: 'grid',
          gridTemplateColumns: `minmax(60px, auto) repeat(${columns.length}, minmax(80px, 1fr))`,
          gridTemplateRows: `auto repeat(${totalSubRows}, auto)`,
          border: 1,
          borderColor: 'divider',
        }}
      >
        <Box
          role="columnheader"
          aria-label="Billing grade"
          sx={{
            bgcolor: 'custom.darkTeal',
            p: 1,
            border: 1,
            borderColor: 'divider',
            gridColumn: '1 / 2',
            gridRow: '1 / 2',
          }}
        />
        {columns.map((col, colIdx) => (
          <Box
            key={`col-${col}`}
            role="columnheader"
            sx={{
              bgcolor: 'custom.darkTeal',
              color: 'common.white',
              p: 1,
              border: 1,
              borderColor: 'divider',
              textAlign: 'center',
              gridColumn: `${colIdx + 2} / ${colIdx + 3}`,
              gridRow: '1 / 2',
            }}
          >
            <Typography variant="overline" component="span">
              {col}
            </Typography>
          </Box>
        ))}

        {groups.map((group, groupIdx) => {
          const priorRows = groups
            .slice(0, groupIdx)
            .reduce((sum, g) => sum + g.rowCount, 0);
          const labelRowStart = priorRows + 2;
          const rowSpan = group.rowCount;

          return (
            <Box key={`group-${group.billingGrade}`} sx={{ display: 'contents' }}>
              <Box
                role="rowheader"
                aria-label={`Billing Grade ${group.billingGrade}`}
                sx={{
                  bgcolor: 'custom.darkTeal',
                  color: 'common.white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 1,
                  border: 1,
                  borderColor: 'divider',
                  gridColumn: '1 / 2',
                  gridRow: `${labelRowStart} / span ${rowSpan}`,
                }}
              >
                <Typography variant="overline" component="span">
                  {group.billingGrade}
                </Typography>
              </Box>

              {columns.map((column, colIdx) => {
                const inRegionRatings = group.inRegionRatingsByColumn.get(column) ?? [];
                const padding = group.rowCount - inRegionRatings.length;

                return Array.from({ length: group.rowCount }).map((_, rowIdx) => {
                  const subRow = labelRowStart + rowIdx;

                  if (rowIdx < padding) {
                    return (
                      <Box
                        key={`pad-${column}-${group.billingGrade}-${rowIdx}`}
                        role="presentation"
                        aria-hidden="true"
                        data-testid={`matrix-pad-${column}-${group.billingGrade}-${rowIdx}`}
                        data-grid-row={subRow}
                        sx={{
                          gridColumn: `${colIdx + 2} / ${colIdx + 3}`,
                          gridRow: `${subRow} / ${subRow + 1}`,
                          bgcolor: 'grey.300',
                          border: 1,
                          borderColor: 'divider',
                          minHeight: 48,
                        }}
                      />
                    );
                  }

                  const rating = inRegionRatings[rowIdx - padding];
                  const key = `${column}|${group.billingGrade}|${rating}`;
                  const cellConsultants = consultantsByCell.get(key) ?? [];
                  const tier = getCellWindowTier(column, rating, gradeConfig.windows);
                  return (
                    <Box
                      key={key}
                      data-grid-row={subRow}
                      sx={{
                        gridColumn: `${colIdx + 2} / ${colIdx + 3}`,
                        gridRow: `${subRow} / ${subRow + 1}`,
                        display: 'flex',
                        '& > *': { flex: 1 },
                      }}
                    >
                      <CalibrationMatrixCell
                        column={column}
                        rating={rating}
                        billingGrade={group.billingGrade}
                        consultants={cellConsultants}
                        windowTier={tier}
                        outOfRegion={false}
                      />
                    </Box>
                  );
                });
              })}
            </Box>
          );
        })}
      </Box>

      {isEmpty && (
        <Box sx={{ mt: 2 }}>
          <EmptyStateMessage message="No consultants match the current filter combination. Adjust filters or check Final Staffing Review flags." />
        </Box>
      )}
    </Box>
  );
}

export default CalibrationMatrixGrid;
